import type { Router, RouteRecordRaw } from 'vue-router';

import { usePermissionStoreWithOut } from '/@/store/modules/permission';

import { whiteNameList } from '/@/enums/pageEnum';
import { useUserStoreWithOut } from '/@/store/modules/user';
import { usePermissionsStoreWithOut } from '/@/store/modules/permissions';
import { useOrgStoreWithOut } from '/@/store/modules/org';
import { usePodPayStoreWithOut } from '/@/store/modules/pod-pay';
import { useRoleStoreWithOut } from '/@/store/modules/role';

import { PAGE_NOT_FOUND_ROUTE } from '/@/router/routes/basic';

import { getAuthManager, getWxAuthManager, getWxSDKManager } from '@lingtu/user';

import { runSchedule } from '../../store/schedules';
import { useCommerceStore } from '/@/store/modules/commerce';
import { dictStore } from '/@/store/modules/dict';

async function handleWxShare(router, to) {
  const wxSdkManager = getWxSDKManager();
  const link = location.origin + router.resolve(to).href;
  const wx = await wxSdkManager.getWx();
  //需在用户可能点击分享按钮前就先调用
  wx.updateAppMessageShareData({
    title:
      '灵图POD设计工具站｜批量采集·设计·提取·抠图·套图·标题提取·上架· 侵权检测· 一站式POD自动设计平台', // 分享标题
    desc: 'POD 卖家设计效率引擎与风险防控中心，提供一站式的设计素材处理、批量生成、版权合规解决方案。支持数据采集、图案提取、抠图、裂变、商品套图、标题生成等多种智能处理功能，助力快速生成印花图、轮播图、检测风险、履约图案。', // 分享描述
    link, // 分享链接，该链接域名或路径必须与当前页面对应的公众号JS安全域名一致
    imgUrl: 'https://static.lingvisions.com/public/0e4e51b2d0574336979d5613b43f0068.png', // 分享图标
    success: function () {},
  });

  // 分享朋友圈
  wx.updateTimelineShareData({
    title:
      '灵图POD设计工具站｜批量采集·设计·提取·抠图·套图·标题提取·上架· 侵权检测· 一站式POD自动设计平台', // 分享标题
    link, // 分享链接，该链接域名或路径必须与当前页面对应的公众号JS安全域名一致
    imgUrl: 'https://static.lingvisions.com/public/0e4e51b2d0574336979d5613b43f0068.png', // 分享图标
    success: function () {},
  });
}

export function createPermissionGuard(router: Router) {
  const userStore = useUserStoreWithOut();
  const commerceStore = useCommerceStore();
  const dict = dictStore();

  const permissionStore = usePermissionStoreWithOut();
  const permissionsStore = usePermissionsStoreWithOut();
  router.beforeEach(async (to, from, next) => {
    const wxAuthManager = getWxAuthManager();

    // 微信浏览器，处理分享
    if (wxAuthManager.isWxBrowser()) {
      handleWxShare(router, to);
    }

    // 白名单路由直接跳过
    if (whiteNameList.includes(to.name as string)) {
      next();
      return;
    }

    const authManager = getAuthManager();
    const token = await authManager.getAccessToken();
    // 不存在token
    if (!token) {
      // 单点登录
      userStore.goLogin();
      return;
    }

    // 微信浏览器环境下，存储openid
    if (wxAuthManager.isNeedSilentWxLogin()) {
      wxAuthManager.silentWxLogin({
        redirectUri:
          location.origin +
          router.resolve({
            name: 'WxLogin',
            query: {
              redirectUri: to.fullPath,
            },
          }).href,
      });
      return;
    }

    if (!userStore.orgs || (!userStore.orgs?.orgs?.length && !userStore.orgs?.personal?.length)) {
      const permissionStore = usePermissionsStoreWithOut();
      const orgStore = useOrgStoreWithOut();

      const orgs = await userStore.getOrgList();
      // 不存在任何组织，走冷启动，进入个人组织创建
      if (!orgs?.orgs?.length && !orgs?.personal?.length) {
        // 如果要去批量生成后台，直接前往落地页
        if (to.fullPath.startsWith('/batch')) {
          location.replace('/pod');
          return;
        }
        next({
          name: 'CreateOrg',
          query: {
            type: 'personal',
            only: true,
            redirectUri: router.resolve(to.fullPath).href,
          },
        });
        return;
      }
      // 存在组织
      else {
        const user = await userStore.getUserInfoAction();

        // 登录态为用户态
        if (!user?.orgId) {
          // 自动绑定一个组织
          await userStore.selectOrg({
            isShowConfirm: false,
            isRedirect: false,
            isUseRecent: true,
          });

          // 获取用户信息、组织信息、权限信息
          await Promise.all([
            userStore.getUserInfoAction(),
            permissionStore.changePermissionCode(),
            orgStore.getOrgInfo(),
            orgStore.getOrgAdminConfig(),
            usePodPayStoreWithOut().getPodPay(),
            commerceStore.getLingdouInfo(),
            dict.getShelfTemplateTypes(),
          ]);
        }
        // 登录态为组织态
        else {
          // 获取组织信息、权限信息
          await Promise.all([
            permissionStore.changePermissionCode(),
            orgStore.getOrgInfo(),
            orgStore.getOrgAdminConfig(),
            usePodPayStoreWithOut().getPodPay(),
            commerceStore.getLingdouInfo(),
            dict.getShelfTemplateTypes(),
          ]);
        }

        const roleStore = useRoleStoreWithOut();

        roleStore.getAllRoles();

        if (!to.fullPath.startsWith('/batch')) {
          // 开始轮询任务
          runSchedule();
        }
      }
    }

    // pod后台
    if (
      ['POD', 'PODExcel'].includes(to.name) &&
      !permissionsStore.hasPermission('createCase').value &&
      !permissionsStore.hasPermission('pod').value
    ) {
      next('/404');
      return;
    }

    // 批量生成后台
    if (to.fullPath.startsWith('/batch') && !permissionsStore.hasPermission('batch').value) {
      // 没有权限，前往落地页
      location.replace('/pod');
      return;
    }

    if (
      to.fullPath.startsWith('/graphic-text') &&
      !permissionsStore.hasPermission('graphicText').value
    ) {
      next('/404');
      return;
    }

    if (permissionStore.getIsDynamicAddedRoute) {
      next();
      return;
    }

    const routes = await permissionStore.buildRoutesAction();

    routes.forEach((route) => {
      router.addRoute(route as unknown as RouteRecordRaw);
    });

    router.addRoute(PAGE_NOT_FOUND_ROUTE as unknown as RouteRecordRaw);

    permissionStore.setDynamicAddedRoute(true);

    if (to.name === PAGE_NOT_FOUND_ROUTE.name) {
      // 动态添加路由后，此处应当重定向到fullPath，否则会加载404页面内容
      next({ path: to.fullPath, replace: true, query: to.query });
    } else {
      const redirectPath = (from.query.redirect || to.path) as string;
      const redirect = decodeURIComponent(redirectPath);
      const nextData = to.path === redirect ? { ...to, replace: true } : { path: redirect };
      next(nextData);
    }
  });
}
